/**
 * 「参考片在拍什么」—— 把拆解报告的**内容层**装成一段人读得懂、可编辑的文本(2026-08-14)。
 *
 * ═══ 为什么要有它 ═══════════════════════════════════════════════════════════
 * 老板拍板:目的地不能只显示拍法/结构,要让用户看见**我们真的看懂了这条爆款在拍什么**;
 * 内容经过我们的编排和生成模型之后本来就不会一模一样,不必藏着。
 *
 * ═══ 它**不进** userInput ══════════════════════════════════════════════════
 * 这段文本走独立通道(`explosiveReferenceContent`),不能混进「想拍什么」那个框。
 * 那个框的值就是 ai 的 `userInput`,`classify_input` 见到长文本会判 SELLING_POINTS,
 * 引擎随即被命令「每个卖点绑定一个节拍、不得新增文案之外的卖点」——
 * 于是成片通篇讲参考片的产品(0812「带过去都是节奏」实证:A/B 28/52 分 vs 拿掉后 65 分)。
 *
 * 分两个框之后:这段告诉引擎「参考片是这么拍的」,userInput 仍是「我的产品事实」,
 * 成片按参考片的拍法与内容组织方式,讲用户自己的产品 —— 这才是「复刻」。
 */
import type { ExplosiveBreakdown } from "./types";

/** 逐拍最多带几拍(整条 15~60s 片通常 7~16 拍;超了文本会长到没人看)。 */
const MAX_BEATS = 16;

/**
 * 拆解报告 → 「参考片在拍什么」正文。
 *
 * 结构刻意贴近用户看视频的顺序:先一句话说这条片在讲什么,再逐拍「画面 + 这拍说了什么」,
 * 最后是记忆点。★不含任何祈使句/标注 —— 提示词工程归 ai 侧,这里只交事实。
 */
export function buildReferenceContent(bd: ExplosiveBreakdown | null | undefined): string {
  if (!bd) return "";
  const lines: string[] = [];
  if (bd.summary) lines.push(`【这条片在讲什么】${bd.summary}`);
  if (bd.hook) lines.push(`【前3秒钩子】${bd.hook}`);

  const beats = (bd.beats ?? []).filter((b) => b && (b.desc || b.voice)).slice(0, MAX_BEATS);
  if (beats.length > 0) {
    lines.push("【逐拍怎么拍的】");
    for (const b of beats) {
      const t = b.t ? `${b.t} ` : "";
      const desc = b.desc ? `画面:${b.desc}` : "";
      const voice = b.voice ? ` | 口播:${b.voice}` : "";
      lines.push(`${t}${desc}${voice}`.trim());
    }
  }

  const points = (bd.explosivePoints ?? []).filter((p) => typeof p === "string" && p.trim());
  if (points.length > 0) {
    lines.push(`【记忆点】${points.join(" / ")}`);
  }
  if (bd.emotion) lines.push(`【情绪基调】${bd.emotion}`);
  if (bd.product) lines.push(`【参考片卖的是】${bd.product}`);
  return lines.join("\n");
}
